import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import {
	OutreachTemplateScopeArgs,
	useCreateOutreachInstructionTemplateMutation,
} from "@/pages/Private/redux/list/list-outreach-template.api";

import { OutreachInstructionTemplate } from "../../../schema/outreach-instruction-template.schema";

interface SaveTemplateModalProps {
	/** The shelf the new template is filed on — kind plus, for COPYWRITING, the list's source type. */
	scope: OutreachTemplateScopeArgs;
	/** The instruction text being saved. */
	body: string;
	/** One line telling the user how far the template will reach ("every list of this type"). */
	scopeHint: string;
	onClose: () => void;
	/** The created template becomes the loaded one. */
	onCreated: (template: OutreachInstructionTemplate) => void;
}

/**
 * *Save as new template* (PREC-3687 AC 9): a name and an optional
 * *Make this the default for new lists*.
 *
 * Mount it conditionally — the shared `Modal` is `keepMounted`, so an always-mounted instance
 * leaves its copy in the DOM even while closed (block 3's lesson).
 *
 * Ticking the default box is what makes a new list of this source type prefill from it; the API
 * clears the scope's previous default in the same transaction, so there is nothing to do here
 * beyond sending the flag.
 */
export const SaveTemplateModal: FunctionComponent<SaveTemplateModalProps> = ({
	scope,
	body,
	scopeHint,
	onClose,
	onCreated,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.outreach.general.saveTemplate.${key}`);

	const [name, setName] = useState("");
	const [isDefault, setIsDefault] = useState(false);
	const [createTemplate, { isLoading }] = useCreateOutreachInstructionTemplateMutation();

	const handleSave = async () => {
		const trimmed = name.trim();

		if (!trimmed) {
			return;
		}

		try {
			const created = await createTemplate({
				kind: scope.kind,
				// Omitted rather than sent as null: the API rejects a SENDER_ROUTING template that
				// carries a source type, because that would hide a company-wide rule.
				...(scope.listSourceType ? { listSourceType: scope.listSourceType } : {}),
				name: trimmed,
				body,
				isDefault,
			}).unwrap();

			onCreated(created);
		} catch {
			// Errors surfaced by the global RTK Query error middleware.
		}
	};

	return (
		<Modal
			disableSave={!name.trim() || isLoading}
			handleClose={onClose}
			handleSave={handleSave}
			isOpened={true}
			isLoading={isLoading}
			submitButtonText={ts("save")}
			title={ts("title")}
		>
			<p className="mb-4 text-xs text-text-meta">{scopeHint}</p>
			<label
				className="label-styles mb-2 block text-sm font-medium text-gray-900"
				htmlFor="outreach-template-name"
			>
				{ts("nameLabel")}
			</label>
			<input
				autoFocus
				className="input-styles mb-4 h-[40px] w-full px-3 text-sm"
				id="outreach-template-name"
				name="outreach-template-name"
				placeholder={ts("namePlaceholder")}
				type="text"
				value={name}
				onChange={(event) => setName(event.target.value)}
			/>
			<Checkbox
				isChecked={isDefault}
				testId="outreach-template-default"
				onChange={() => setIsDefault((current) => !current)}
			>
				{ts("makeDefault")}
			</Checkbox>
		</Modal>
	);
};
